import axios from "./axios";

/** Soft-error envelope that preserves HTTP status for 403 / not_authorized detection. */
function normalizeOfferApiError(error, fallbackMessage) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return { ...data, status };
  }
  if (typeof data === "string" && data.trim()) {
    return { error: data, status };
  }
  return { error: fallbackMessage, status };
}

export async function deleteOffer(payload) {
  return axios
    .delete(`/offers/` + payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || { error: "Failed to delete offer" });
    });
}

export async function createOffer(payload) {
  return axios
    .post(`/offers/`, { offer: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      // Return error object with proper structure for Redux Toolkit to handle
      if (error.response) {
        // Server responded with error status
        return Promise.reject({
          response: {
            status: error.response.status,
            data: error.response.data,
          },
          message: error.message,
        });
      } else if (error.request) {
        // Request was made but no response received
        return Promise.reject({
          response: {
            status: 0,
            data: { error: "No response from server" },
          },
          message: "Network error",
        });
      } else {
        // Something else happened
        return Promise.reject({
          response: {
            status: 500,
            data: { error: error.message },
          },
          message: error.message,
        });
      }
    });
}

export async function updateOffer(payload) {
  return axios
    .put(`/offers/` + payload.id, { offer: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      // Return error object with proper structure for Redux Toolkit to handle
      if (error.response) {
        // Server responded with error status
        return Promise.reject({
          response: {
            status: error.response.status,
            data: error.response.data,
          },
          message: error.message,
        });
      } else if (error.request) {
        // Request was made but no response received
        return Promise.reject({
          response: {
            status: 0,
            data: { error: "No response from server" },
          },
          message: "Network error",
        });
      } else {
        // Something else happened
        return Promise.reject({
          response: {
            status: 500,
            data: { error: error.message },
          },
          message: error.message,
        });
      }
    });
}

export async function fetchOffer(payload) {
  // payload is only ID. Bust caches so tab-switch after calc save is not stale.
  return axios
    .get(`/offers/` + payload, {
      params: { t: Date.now() },
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || { error: "Failed to fetch offer" });
    });
}

export async function fetchOffers(controller) {
  return axios({ method: "get", url: "/offers/list", params: controller })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || { error: "Failed to fetch offers" });
    });
}

export async function sendOffer(payload) {
  return axios
    .post(`/offers/${payload.id}/send_offer`, { offer: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(normalizeOfferApiError(error, "Failed to send offer"));
    });
}

export async function getPdfOffer(payload) {
  return axios
    .get(`/offers/${payload.id}/pdf_offer`, { params: payload.params })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(normalizeOfferApiError(error, "Failed to get PDF offer"));
    });
}

export async function checkSendOfferStatus(jobId) {
  return axios
    .get(`/offers/send_offer_status/${jobId}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(normalizeOfferApiError(error, "Failed to check send status"));
    });
}

/** GET /offers/:id/revisions — list (flag offer_revisions). */
export async function fetchOfferRevisions(offerId) {
  return axios
    .get(`/offers/${offerId}/revisions`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(normalizeOfferApiError(error, "Failed to fetch offer revisions"));
    });
}

/**
 * GET /offers/:id/activity — Claude-like activity timeline.
 * Query: category=all|kalkyle|revisjoner|import|godkjenning
 * 404 / missing endpoint → empty data (dev-friendly).
 */
export async function fetchOfferActivity(offerId, { category } = {}) {
  const params = {};
  if (category && category !== "all") {
    params.category = category;
  }
  return axios
    .get(`/offers/${offerId}/activity`, { params })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      if (error?.response?.status === 404) {
        return { data: [], missing: true, status: 404 };
      }
      return Promise.reject(normalizeOfferApiError(error, "Failed to fetch offer activity"));
    });
}

/** GET /offers/:id/revisions/:revisionId — show with lines + snapshot_data. */
export async function fetchOfferRevision(offerId, revisionId) {
  return axios
    .get(`/offers/${offerId}/revisions/${revisionId}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(normalizeOfferApiError(error, "Failed to fetch offer revision"));
    });
}

/**
 * POST /offers/:id/revisions — create/refresh draft.
 * Body: { based_on_revision_id?, change_note?, replace_draft? }
 */
export async function createOfferRevision(offerId, payload = {}) {
  return axios
    .post(`/offers/${offerId}/revisions`, payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(normalizeOfferApiError(error, "Failed to create offer revision"));
    });
}

/**
 * POST /offers/:id/accept_offer — sets accepted_revision_id.
 * payload: { id, revision_id? }
 */
export async function acceptOffer(payload) {
  const { id, revision_id, ...rest } = payload || {};
  return axios
    .post(`/offers/${id}/accept_offer`, {
      revision_id,
      ...rest,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(normalizeOfferApiError(error, "Failed to accept offer"));
    });
}

/**
 * POST /offers/:offerId/revisions/:revisionId/approve
 * Soft-handles 404 while the endpoint rolls out.
 * Expected: { revision?, approved_revision?, draft_revision?, offer?, current_draft_revision_id? }
 */
export async function approveOfferRevision(offerId, revisionId, payload = {}) {
  try {
    const response = await axios.post(
      `/offers/${offerId}/revisions/${revisionId}/approve`,
      payload
    );
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      return { status: 404, missing: true };
    }
    return Promise.reject(normalizeOfferApiError(error, "Failed to approve offer revision"));
  }
}

/**
 * POST /offers/:offerId/revisions/:revisionId/reject
 * Soft-handles 404 while the endpoint rolls out.
 * Expected: { revision?, rejected_revision?, offer? }
 */
export async function rejectOfferRevision(offerId, revisionId, payload = {}) {
  try {
    const response = await axios.post(
      `/offers/${offerId}/revisions/${revisionId}/reject`,
      payload
    );
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      return { status: 404, missing: true };
    }
    return Promise.reject(normalizeOfferApiError(error, "Failed to reject offer revision"));
  }
}

/**
 * POST /offers/:offerId/revisions/:revisionId/submit_for_review
 * Internal review (pending_review) — not customer send.
 * Soft-handles 404 while the endpoint rolls out.
 * Body: { reviewer_id? }
 * Expected: { success?, revision?, offer?, current_draft_revision_id?, message? }
 */
export async function submitOfferRevisionForReview(
  offerId,
  revisionId,
  payload = {}
) {
  try {
    const response = await axios.post(
      `/offers/${offerId}/revisions/${revisionId}/submit_for_review`,
      payload
    );
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      return { status: 404, missing: true };
    }
    return Promise.reject(normalizeOfferApiError(
      error,
      "Failed to submit offer revision for review"
    ));
  }
}

/**
 * POST /offers/:id/convert_to_project — behind offer_project_conversion_v2.
 * payload: { id, idempotency_key? }
 */
export async function convertOfferToProject(payload) {
  const { id, idempotency_key, ...rest } = payload || {};
  return axios
    .post(`/offers/${id}/convert_to_project`, {
      idempotency_key,
      ...rest,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(normalizeOfferApiError(
        error,
        "Failed to convert offer to project"
      ));
    });
}

/** POST /offers/:id/analyze_document — returns review-only AI suggestions. */
export async function analyzeOfferDocument(offerId, payload) {
  return axios
    .post(`/offers/${offerId}/analyze_document`, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to analyze offer document"))
    );
}

/** POST /ai_generator/generate fallback while the dedicated endpoint rolls out. */
export async function analyzeOfferDocumentFallback(offerId, payload = {}) {
  return axios
    .post("/ai_generator/generate", {
      module: "offer_document",
      context: {
        offer_id: offerId,
        document_text: payload.document_text,
        offer_title: payload.offer_title,
        customer_name: payload.customer_name,
        project_name: payload.project_name,
        reference: payload.reference,
        comment: payload.comment,
        existing_line_titles: payload.existing_line_titles,
      },
      fields_to_generate: ["analysis"],
    })
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to analyze offer document"))
    );
}

/** Offer checklist endpoints. The read endpoint has a legacy-compatible fallback. */
export async function createOfferChecklist(offerId, payload = {}) {
  const body = payload?.offer ? payload : { offer: payload || {} };
  return axios
    .post(`/offers/${offerId}/create_checklist`, body)
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to create offer checklist"))
    );
}

export async function fetchOfferChecklist(offerId, params = {}) {
  try {
    const response = await axios.get(`/offers/${offerId}/get_checklist`, {
      params,
    });
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    // Missing checklist is a normal empty state, not an error for the tab.
    if (status === 404) {
      return { status: 404, missing: true };
    }
    return Promise.reject(normalizeOfferApiError(error, "Failed to load offer checklist"));
  }
}

export async function updateOfferChecklist(offerId, payload) {
  const body = payload?.offer ? payload : { offer: payload || {} };
  return axios
    .patch(`/offers/${offerId}/update_checklist`, body)
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to update offer checklist"))
    );
}

export async function deleteOfferChecklist(offerId) {
  return axios
    .delete(`/offers/${offerId}/delete_checklist`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to delete offer checklist"))
    );
}

/**
 * POST /offers/:id/generate_lines_from_calculation
 * Returns offer JSON with customer-facing items (incl. visible_to_customer).
 * Soft-handles 404 while the endpoint rolls out.
 */
export async function generateOfferLinesFromCalculation(offerId, options = {}) {
  try {
    const response = await axios.post(
      `/offers/${offerId}/generate_lines_from_calculation`, options
    );
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      return { status: 404, missing: true };
    }
    return Promise.reject(normalizeOfferApiError(
      error,
      "Failed to generate offer lines from calculation"
    ));
  }
}

/**
 * GET /offers/:id/customer_presentation — ADR-005 kundebilde fra kalkyle + regler.
 */
export async function fetchOfferCustomerPresentation(offerId, { bust } = {}) {
  try {
    const response = await axios.get(`/offers/${offerId}/customer_presentation`, {
      params: { t: bust ?? Date.now() },
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    });
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      return { items: [], missing: true, status: 404 };
    }
    return normalizeOfferApiError(error, "Failed to fetch customer presentation");
  }
}

/**
 * GET /offers/:id/template_versions — offer template version history.
 * → { data: [{ id, version_number, created_by, change_note, created_at }] }
 * Soft-handles 404 while the endpoint rolls out (offer_template_versions flag).
 */
export async function fetchOfferTemplateVersions(offerId) {
  try {
    const response = await axios.get(`/offers/${offerId}/template_versions`);
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      return { data: [], missing: true, status: 404 };
    }
    return Promise.reject(normalizeOfferApiError(error, "Failed to fetch offer template versions"));
  }
}

/** POST /offers/:id/restore_template_version — body `{ version_id }`. */
export async function restoreOfferTemplateVersion(offerId, versionId) {
  return axios
    .post(`/offers/${offerId}/restore_template_version`, {
      version_id: versionId,
    })
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to restore offer template version"))
    );
}

/**
 * POST /offers/:id/save_as_template — «Lagre som tilbudsmal».
 * body `{ reference?, change_note? }`.
 */
export async function saveOfferAsTemplate(offerId, payload = {}) {
  return axios
    .post(`/offers/${offerId}/save_as_template`, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to save offer as template"))
    );
}

/**
 * POST /offers/:id/save_to_template — «Oppdater tilbudsmal».
 * body `{ template_id, change_note? }`.
 */
export async function saveOfferToTemplate(offerId, payload = {}) {
  return axios
    .post(`/offers/${offerId}/save_to_template`, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to save offer to template"))
    );
}

/**
 * POST /offers/:id/save_as — «Lagre som nytt tilbud».
 * body `{ reference?, customer_id?, project_id? }`.
 */
export async function saveOfferAs(offerId, payload = {}) {
  return axios
    .post(`/offers/${offerId}/save_as`, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to save offer as new offer"))
    );
}

/**
 * POST /offers/:id/overwrite — «Overskriv eksisterende tilbud».
 * body `{ target_offer_id, confirm: true }`.
 */
export async function overwriteOffer(offerId, targetOfferId) {
  return axios
    .post(`/offers/${offerId}/overwrite`, {
      target_offer_id: targetOfferId,
      confirm: true,
    })
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeOfferApiError(error, "Failed to overwrite offer")));
}

/** GET /offers/:id/inquiries — list (flag offer_inquiries). */
export async function fetchOfferInquiries(offerId) {
  return axios
    .get(`/offers/${offerId}/inquiries`)
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferApiError(error, "Failed to fetch offer inquiries")
    );
}

/** POST /offers/:id/inquiries */
export async function createOfferInquiry(offerId, payload = {}) {
  return axios
    .post(`/offers/${offerId}/inquiries`, { inquiry: payload })
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferApiError(error, "Failed to create offer inquiry")
    );
}

/** PATCH /offers/:id/inquiries/:inquiryId */
export async function updateOfferInquiry(offerId, inquiryId, payload = {}) {
  return axios
    .patch(`/offers/${offerId}/inquiries/${inquiryId}`, { inquiry: payload })
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferApiError(error, "Failed to update offer inquiry")
    );
}

/** DELETE /offers/:id/inquiries/:inquiryId */
export async function deleteOfferInquiry(offerId, inquiryId) {
  return axios
    .delete(`/offers/${offerId}/inquiries/${inquiryId}`)
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferApiError(error, "Failed to delete offer inquiry")
    );
}
